import { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { formatErrorForLogging } from '@eluve/eluve-errors';

import { getAppointmentNoteSignedState } from './appointment-lifecycle';

type SignedCheckLogger = {
  warn: (message: string, meta?: Record<string, unknown>) => void;
};

/**
 * Network-only read of whether an appointment's clinical note is signed, for
 * the extension's record / complete guards. [ENG-5258]
 *
 * Fail-open by design: any query failure resolves to `false` (treated as not
 * signed) so a transient network blip never blocks a legitimate recording or
 * completion. The backend is the authoritative stop — `setAppointmentCompleted`
 * skips a signed appointment and `prevent_modifications_to_signed_human_outputs`
 * rejects the note write regardless of what the client decides here.
 */
export const isAppointmentNoteSigned = async ({
  client,
  appointmentId,
  tenantId,
  logger,
}: {
  client: ApolloClient<NormalizedCacheObject>;
  appointmentId: string;
  tenantId: string;
  logger?: SignedCheckLogger;
}): Promise<boolean> => {
  try {
    const { data } = await client.query({
      query: getAppointmentNoteSignedState,
      variables: { appointmentId, tenantId },
      // Signing may have happened on another surface (web app / another tab)
      // during this recording session — bypass the cache so we see it.
      fetchPolicy: 'network-only',
    });

    return Boolean(data?.appointmentsByPk?.doctor_interaction?.noteSignedAt);
  } catch (error) {
    logger?.warn(
      'Signed-state check failed; proceeding without blocking (fail-open)',
      { appointmentId, tenantId, error: formatErrorForLogging(error) },
    );
    return false;
  }
};
